/**
 * Hybrid in-memory + sessionStorage cache for HelpScout API responses
 * Reduces API calls and improves performance
 * SessionStorage persists during browsing session (survives page refresh)
 */
class HelpScoutCache {
  constructor() {
    this.cache = new Map();
    this.ttl = 5 * 60 * 1000; // 5 minutes default TTL
    this.storagePrefix = 'helpscout:cache:';
    this.version = '1.0';
    this.storageAvailable = this.isSessionStorageAvailable();
  }

  /**
   * Check if sessionStorage is available
   */
  isSessionStorageAvailable() {
    try {
      const test = '__storage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      console.warn('[HelpScout Cache] SessionStorage not available, using memory-only cache');
      return false;
    }
  }

  /**
   * Generate cache key from endpoint and params
   */
  generateKey(endpoint, params = {}) {
    const paramString = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return `${endpoint}?${paramString}`;
  }

  /**
   * Get storage key with prefix
   */
  getStorageKey(key) {
    return `${this.storagePrefix}${key}`;
  }

  /**
   * Get data from sessionStorage
   */
  getFromSessionStorage(key) {
    if (!this.storageAvailable) return null;

    try {
      const storageKey = this.getStorageKey(key);
      const item = sessionStorage.getItem(storageKey);
      if (!item) return null;

      const parsed = JSON.parse(item);
      
      // Check version for future cache invalidation
      if (parsed.version !== this.version) {
        sessionStorage.removeItem(storageKey);
        return null;
      }

      // Check expiry
      const now = Date.now();
      if (now > parsed.expiry) {
        sessionStorage.removeItem(storageKey);
        return null;
      }

      return parsed.data;
    } catch (e) {
      console.warn('[HelpScout Cache] Error reading from sessionStorage:', e);
      return null;
    }
  }

  /**
   * Set data in sessionStorage
   */
  setToSessionStorage(key, data, expiry) {
    if (!this.storageAvailable) return;

    try {
      const storageKey = this.getStorageKey(key);
      const item = {
        data,
        expiry,
        version: this.version,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(item));
    } catch (e) {
      // Handle QuotaExceededError or other errors gracefully
      if (e.name === 'QuotaExceededError') {
        console.warn('[HelpScout Cache] SessionStorage quota exceeded, clearing old entries');
        this.clearSessionStorage();
      } else {
        console.warn('[HelpScout Cache] Error writing to sessionStorage:', e);
      }
    }
  }

  /**
   * Get cached data if not expired
   * Priority: Memory → sessionStorage → null
   */
  get(key) {
    // Check memory cache first (fastest)
    const cached = this.cache.get(key);
    if (cached) {
      const now = Date.now();
      if (now > cached.expiry) {
        this.cache.delete(key);
      } else {
        return cached.data;
      }
    }

    // Check sessionStorage (survives page refresh)
    const sessionData = this.getFromSessionStorage(key);
    if (sessionData) {
      // Restore to memory cache for faster subsequent access
      const expiry = Date.now() + this.ttl;
      this.cache.set(key, { data: sessionData, expiry });
      return sessionData;
    }

    return null;
  }

  /**
   * Set data in cache with expiry
   * Writes to both memory and sessionStorage
   */
  set(key, data, ttl = this.ttl) {
    const expiry = Date.now() + ttl;
    
    // Store in memory
    this.cache.set(key, { data, expiry });
    
    // Store in sessionStorage
    this.setToSessionStorage(key, data, expiry);
  }

  /**
   * Clear entire cache (both memory and sessionStorage)
   */
  clear() {
    this.cache.clear();
    this.clearSessionStorage();
  }

  /**
   * Clear all sessionStorage entries with our prefix
   */
  clearSessionStorage() {
    if (!this.storageAvailable) return;

    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
    } catch (e) {
      console.warn('[HelpScout Cache] Error clearing sessionStorage:', e);
    }
  }

  /**
   * Clear cache entries matching pattern
   */
  clearPattern(pattern) {
    const regex = new RegExp(pattern);
    
    // Clear from memory
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
    
    // Clear from sessionStorage
    if (this.storageAvailable) {
      try {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const storageKey = sessionStorage.key(i);
          if (storageKey && storageKey.startsWith(this.storagePrefix)) {
            const key = storageKey.substring(this.storagePrefix.length);
            if (regex.test(key)) {
              keysToRemove.push(storageKey);
            }
          }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
      } catch (e) {
        console.warn('[HelpScout Cache] Error clearing pattern from sessionStorage:', e);
      }
    }
  }
}

export default new HelpScoutCache();

